import { useState } from 'react';
import { Button, Divider, Input, Modal, Progress, Space, Upload, type UploadProps } from 'antd';
import { ImagePlus, Link as LinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const { Dragger } = Upload;

export interface ImageInsertDialogProps {
  open: boolean;
  onClose: () => void;
  /** Insert the resolved image URL into the editor. */
  onInsert: (url: string) => void;
  /** Uploads a file via the app's file service and resolves the public URL. */
  uploadImage: (file: File, onProgress?: (percent: number) => void) => Promise<string>;
}

/**
 * Image insert dialog for the rich text editor — upload a file (drag/drop or
 * pick) through the real file service, or paste an existing URL. The upload
 * path returns a server-issued public URL so the editor never embeds base64.
 */
export function ImageInsertDialog({ open, onClose, onInsert, uploadImage }: ImageInsertDialogProps) {
  const { t } = useTranslation('components');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setUrl('');
    setUploading(false);
    setProgress(0);
  };

  const close = () => {
    if (uploading) return; // don't drop an in-flight upload
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error(t('richTextEditor.imageInvalidType'));
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const src = await uploadImage(file, setProgress);
      if (!src) throw new Error('empty url');
      onInsert(src);
      reset();
      onClose();
    } catch {
      toast.error(t('richTextEditor.imageUploadError'));
      setUploading(false);
    }
  };

  const insertUrl = () => {
    const v = url.trim();
    if (!v) return;
    onInsert(v);
    reset();
    onClose();
  };

  const draggerProps: UploadProps = {
    multiple: false,
    showUploadList: false,
    accept: 'image/*',
    disabled: uploading,
    // Return false to keep AntD from auto-POSTing; we drive the upload ourselves.
    beforeUpload: (file) => {
      void handleFile(file);
      return false;
    },
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title={t('richTextEditor.imageDialogTitle')}
      footer={null}
      maskClosable={!uploading}
      destroyOnHidden
      width={460}
    >
      <Dragger {...draggerProps} style={{ padding: '12px 8px' }}>
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <ImagePlus size={30} />
        </p>
        <p className="ant-upload-text">{t('richTextEditor.imageUploadHint')}</p>
        <p className="ant-upload-hint" style={{ fontSize: 12 }}>
          {t('richTextEditor.imageUploadSub')}
        </p>
      </Dragger>

      {uploading ? <Progress percent={progress} size="small" style={{ marginTop: 12 }} /> : null}

      <Divider plain style={{ margin: '16px 0' }}>
        {t('richTextEditor.imageOr')}
      </Divider>

      <Space.Compact style={{ width: '100%' }}>
        <Input
          prefix={<LinkIcon size={14} />}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onPressEnter={insertUrl}
          placeholder={t('richTextEditor.imageUrlPlaceholder')}
          disabled={uploading}
          allowClear
        />
        <Button type="primary" onClick={insertUrl} disabled={uploading || url.trim().length === 0}>
          {t('richTextEditor.imageInsert')}
        </Button>
      </Space.Compact>
    </Modal>
  );
}
